// RepairDialog — modal that runs an AI repair attempt + shows before/after
// to the user for accept / reject.
//
// Session 19AU · Self-Healing Repair Layer (user-triggered mode):
//
//   FlagButton "AI 修复" → opens this dialog → user describes problem →
//   call /repair/item → render AI original alongside repaired version →
//   user accepts (writes to progress.repairs) or rejects (record kept in
//   attempts[], current stays null).
//
// UX flow (4 phases driven by `phase` state):
//   compose  → user types problem note + sees budget remaining
//   running  → spinner while LLM call in flight
//   review   → diff view + accept/reject buttons
//   blocked  → shown when budget exhausted or LLM unavailable

import { useState } from 'react';
import { X, Sparkles, Check, Ban, AlertTriangle } from 'lucide-react';
// Isomorphism Phase 3: Zustand primary (replacing Context).
import { useStoreCourseData } from '../lib/hooks/use-store-course.js';
import { useStore } from '../lib/store/index.js';
import { useNotify } from '../lib/hooks/use-notify.js';
import { useToast } from './toast.js';
import { useT } from '../i18n/index.js';
import { recordEvent } from '../storage/idb.js';
import { postRepair, mapRepairErrorToMessage, type RepairableKind, type RepairResponse } from '../api/repair.js';
import {
  checkRepairBudget,
  recordRepairAttempt,
  acceptRepairCurrent,
  buildPreviousAttemptsHints,
} from '../lib/repair-store.js';
import { getRepairRecord } from '../lib/resolve-item.js';
import { PER_ITEM_REPAIR_CAP_RMB, PER_COURSE_REPAIR_CAP_RMB, courseRepairTotalRmb } from '@examforge/shared/schemas';

export interface RepairDialogProps {
  open: boolean;
  onClose(): void;
  kind: RepairableKind;
  itemId: string;
  /** Current item JSON the user is looking at (resolved — may be a prior
   *  repair, not the AI original). */
  itemJson: unknown;
  /** Optional context for the LLM (siblings + source slice). */
  contextItems?: unknown[];
  sourceSlice?: string;
}

type Phase =
  | { kind: 'compose' }
  | { kind: 'blocked'; reason: string }
  | { kind: 'running' }
  | { kind: 'review'; response: RepairResponse }
  | { kind: 'error'; message: string };

export function RepairDialog({
  open, onClose, kind, itemId, itemJson, contextItems, sourceSlice,
}: RepairDialogProps) {
  const { draft, progress, refreshProgress } = useStoreCourseData();
  const toast = useToast();
  const notify = useNotify();
  const t = useT();
  const [note, setNote] = useState('');
  const [phase, setPhase] = useState<Phase>({ kind: 'compose' });

  if (!open) return null;

  const existingRecord = getRepairRecord(kind, itemId, progress.repairs);
  const itemUsed = existingRecord?.budgetUsedRmb ?? 0;
  const courseUsed = courseRepairTotalRmb(progress.repairs ?? {});

  async function startRepair(): Promise<void> {
    if (!note.trim()) {
      toast.warn(t('repair.needDetail'));
      return;
    }
    const blocked = checkRepairBudget({ kind, itemId, repairs: progress.repairs });
    if (blocked) {
      const msg = blocked.code === 'item_cap'
        ? t('repair.budgetBlockedItem').replace('{used}', `$${blocked.usedUsd.toFixed(4)}`).replace('{cap}', `$${blocked.capUsd.toFixed(2)}`)
        : t('repair.budgetBlockedCourse').replace('{used}', `$${blocked.usedUsd.toFixed(4)}`).replace('{cap}', `$${blocked.capUsd.toFixed(2)}`);
      setPhase({ kind: 'blocked', reason: msg });
      return;
    }

    setPhase({ kind: 'running' });
    // 19BN Phase 2: preflight credit check before repair (~3 points).
    const balance = useStore.getState().balance;
    if (balance !== null && balance < 3) {
      setPhase({ kind: 'error', message: t('account.insufficientCredits') });
      return;
    }
    const startedAt = new Date().toISOString();
    // 19AW Layer-3: build retry diagnostics from prior attempts on this item.
    // Empty on the first attempt (which is the byte-stable cache-friendly
    // common path); populated only when the user has been here before.
    const previousAttempts = buildPreviousAttemptsHints(existingRecord);
    try {
      const response = await postRepair({
        kind,
        itemJson,
        reason: { mode: 'user_report', userMessage: note.trim() },
        ...(contextItems ? { contextItems } : {}),
        ...(sourceSlice ? { sourceSlice } : {}),
        ...(previousAttempts.length > 0 ? { previousAttempts } : {}),
      });
      // Persist the attempt regardless of outcome — we want failed attempts
      // visible in the operator dashboard for prompt-tuning insight.
      // 19AW: forward guardReport / failureKind / promptVersion from response.
      const attemptResult = mapResponseToAttemptResult(response);
      const guardReport = response.status === 'success' ? response.guardReport : undefined;
      const failureKind = response.status !== 'provider_error' ? response.failureKind : undefined;
      const promptVersion = response.status !== 'provider_error' ? response.promptVersion : undefined;
      await recordRepairAttempt({
        courseId: draft.id,
        kind,
        itemId,
        attempt: {
          attemptedAt: startedAt,
          reason: { mode: 'user_report', userMessage: note.trim() },
          result: attemptResult,
          costRmb: response.status === 'provider_error' ? 0 : response.costRmb,
          inputTokens: response.status === 'provider_error' ? 0 : response.inputTokens,
          outputTokens: response.status === 'provider_error' ? 0 : response.outputTokens,
          durationMs: response.durationMs,
          ...(response.status !== 'provider_error' ? { providerName: response.providerName } : {}),
          acceptedByUser: false,
          ...(guardReport !== undefined ? { guardReport } : {}),
          ...(failureKind !== undefined ? { failureKind } : {}),
          ...(promptVersion !== undefined ? { promptVersion } : {}),
        },
      });
      void recordEvent({
        type: 'flag', // reuse — represents a repair-triggering user action
        summary: `repair attempted (${response.status})`,
        courseId: draft.id,
        itemId,
        itemField: 'repair',
        viewPath: typeof window !== 'undefined' ? window.location.pathname : undefined,
        payload: {
          kind,
          status: response.status,
          ...(response.status !== 'provider_error' ? {
            costRmb: response.costRmb,
            modelKey: response.modelKey,
            inputTokens: response.inputTokens,
            outputTokens: response.outputTokens,
          } : {}),
          note: note.trim().slice(0, 500),
        },
      });
      await refreshProgress();
      setPhase({ kind: 'review', response });
    } catch (err) {
      // 19BL Session 3b: code→message mapping extracted to mapRepairErrorToMessage
      // (api/repair.ts) — was inline here with zero test coverage. The pure
      // function is pinned by tests/lib/repair-error-message.test.ts.
      setPhase({ kind: 'error', message: mapRepairErrorToMessage(err) });
      // 19BN Phase 5: notify message center on repair failure.
      notify({ source: 'system', level: 'error', title: 'Repair failed', message: String((err as Error)?.message ?? ''), persistent: true });
    }
  }

  async function acceptRepair(output: unknown): Promise<void> {
    await acceptRepairCurrent({ courseId: draft.id, kind, itemId, output });
    void recordEvent({
      type: 'flag',
      summary: `repair accepted by user`,
      courseId: draft.id,
      itemId,
      itemField: 'repair',
      viewPath: typeof window !== 'undefined' ? window.location.pathname : undefined,
      payload: { kind, action: 'accept' },
    });
    await refreshProgress();
    toast.success(t('repair.applied'));
    // 19BN Phase 5: notify message center on repair success.
    notify({ source: 'system', level: 'success', title: 'Repair applied', message: 'Item updated successfully', persistent: true });
    // 19BN Phase D D6: notify points deducted for the repair (info · the cost
    // is already reflected in the balance hero; this is the audit record).
    notify({ source: 'account', level: 'info', title: '点数已扣除', message: '修复生成', persistent: false });
    onClose();
    setPhase({ kind: 'compose' });
    setNote('');
  }

  function rejectRepair(): void {
    void recordEvent({
      type: 'flag',
      summary: `repair rejected by user`,
      courseId: draft.id,
      itemId,
      itemField: 'repair',
      viewPath: typeof window !== 'undefined' ? window.location.pathname : undefined,
      payload: { kind, action: 'reject' },
    });
    toast.info(t('repair.keptOriginal'));
    onClose();
    setPhase({ kind: 'compose' });
    setNote('');
  }

  return (
    <div className="repair-overlay" role="dialog" aria-modal="true" aria-label={t('repair.ariaLabel')}>
      <div className="repair-dialog">
        <header className="repair-dialog-head">
          <Sparkles size={16} aria-hidden="true" />
          <h2>{t('repair.title')}</h2>
          <button
            type="button"
            className="repair-dialog-close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X size={14} aria-hidden="true" />
          </button>
        </header>

        <div className="repair-dialog-budget">
          <span>{t('repair.itemBudget').replace('{used}', itemUsed.toFixed(4)).replace('{cap}', PER_ITEM_REPAIR_CAP_RMB.toFixed(2))}</span>
          <span>·</span>
          <span>{t('repair.courseBudget').replace('{used}', courseUsed.toFixed(4)).replace('{cap}', PER_COURSE_REPAIR_CAP_RMB.toFixed(2))}</span>
        </div>

        {phase.kind === 'compose' && (
          <div className="repair-dialog-body">
            <p className="repair-dialog-help">
              {t('repair.hint')}
            </p>
            <textarea
              className="repair-dialog-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('repair.placeholder')}
              rows={5}
              autoFocus
            />
            <div className="repair-dialog-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
              <button type="button" className="btn-primary" onClick={() => { void startRepair(); }}>
                <Sparkles size={13} aria-hidden="true" /> {t('repair.submit')}
              </button>
            </div>
          </div>
        )}

        {phase.kind === 'blocked' && (
          <div className="repair-dialog-body repair-dialog-blocked">
            <Ban size={20} aria-hidden="true" />
            <p>{phase.reason}</p>
            <button type="button" className="btn-secondary" onClick={onClose}>{t('common.close')}</button>
          </div>
        )}

        {phase.kind === 'running' && (
          <div className="repair-dialog-body repair-dialog-running">
            <div className="repair-dialog-spinner" aria-hidden="true" />
            <p>{t('repair.loading')}</p>
          </div>
        )}

        {phase.kind === 'error' && (
          <div className="repair-dialog-body repair-dialog-error">
            <AlertTriangle size={20} aria-hidden="true" />
            <p>{phase.message}</p>
            <div className="repair-dialog-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>{t('common.close')}</button>
              <button type="button" className="btn-primary" onClick={() => setPhase({ kind: 'compose' })}>
                {t('common.retry')}
              </button>
            </div>
          </div>
        )}

        {phase.kind === 'review' && (
          <RepairReview
            response={phase.response}
            originalJson={itemJson}
            onAccept={(out) => { void acceptRepair(out); }}
            onReject={rejectRepair}
            onRetry={() => { setPhase({ kind: 'compose' }); }}
          />
        )}
      </div>
    </div>
  );
}

function RepairReview({
  response, originalJson, onAccept, onReject, onRetry,
}: {
  response: RepairResponse;
  originalJson: unknown;
  onAccept(output: unknown): void;
  onReject(): void;
  onRetry(): void;
}) {
  const t = useT();
  if (response.status === 'provider_error') {
    return (
      <div className="repair-dialog-body repair-dialog-error">
        <AlertTriangle size={20} aria-hidden="true" />
        <p>{t('repair.llmFailedPrefix')}{response.message}</p>
        <button type="button" className="btn-primary" onClick={onRetry}>{t('common.retry')}</button>
      </div>
    );
  }
  const output = response.status === 'success' ? response.output : (response as { rawOutput: unknown }).rawOutput;
  const schemaFail = response.status === 'schema_fail';
  return (
    <div className="repair-dialog-body">
      <div className="repair-dialog-meta">
        <span>cost: <code>${response.costRmb.toFixed(4)}</code></span>
        <span>·</span>
        <span>tokens: in {response.inputTokens} / out {response.outputTokens}</span>
        <span>·</span>
        <span>{response.durationMs}ms · {response.modelKey}</span>
      </div>
      {schemaFail && (
        <div className="repair-dialog-schema-warn">
          <AlertTriangle size={13} aria-hidden="true" />
          {t('repair.schemaWarn')}
        </div>
      )}
      <div className="repair-dialog-diff">
        <div className="repair-dialog-diff-col">
          <h3>{t('repair.currentVersion')}</h3>
          <pre>{stableStringify(originalJson)}</pre>
        </div>
        <div className="repair-dialog-diff-col">
          <h3>{t('repair.repairedVersion')}</h3>
          <pre>{stableStringify(output)}</pre>
        </div>
      </div>
      <div className="repair-dialog-actions">
        <button type="button" className="btn-secondary" onClick={onReject}>
          <X size={13} aria-hidden="true" /> {t('repair.keepOriginal')}
        </button>
        <button type="button" className="btn-secondary" onClick={onRetry}>
          {t('repair.retryPhrase')}
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={() => onAccept(output)}
          disabled={schemaFail}
          title={schemaFail ? t('repair.schemaRetryTitle') : undefined}
        >
          <Check size={13} aria-hidden="true" /> {t('repair.accept')}
        </button>
      </div>
    </div>
  );
}

function stableStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function mapResponseToAttemptResult(r: RepairResponse): import('@examforge/shared/schemas').RepairAttemptResult {
  if (r.status === 'success') return { status: 'success', output: r.output };
  if (r.status === 'schema_fail') return { status: 'schema_fail', schemaErrors: r.schemaErrors };
  return { status: 'provider_error', message: r.message };
}
